import React, { useState, useEffect, useCallback } from "react";
import {
  GraduationCap, Play, FileText, Link2, Plus, Trash2, Award,
  Loader2, ChevronRight, CheckCircle, XCircle, Clock, BarChart3,
  Upload, Video, BookOpen, Users, Star, Trophy
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Video Embed ───
const VideoEmbed = ({ url, embedType }) => {
  if (embedType === "youtube") {
    let videoId = "";
    if (url.includes("youtu.be/")) videoId = url.split("youtu.be/")[1]?.split("?")[0];
    else if (url.includes("v=")) videoId = url.split("v=")[1]?.split("&")[0];
    if (!videoId) return <a href={url} target="_blank" rel="noreferrer" className="text-xs text-[#E83E8C] hover:underline">{url}</a>;
    return <iframe src={`https://www.youtube.com/embed/${videoId}`} className="w-full aspect-video rounded-xl" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen title="Training Video" />;
  }
  if (embedType === "google_drive") {
    const fileId = url.match(/\/d\/([a-zA-Z0-9_-]+)/)?.[1];
    if (fileId) return <iframe src={`https://drive.google.com/file/d/${fileId}/preview`} className="w-full aspect-video rounded-xl" allow="autoplay" title="Training Content" />;
  }
  if (embedType === "vimeo") {
    const vimeoId = url.split("vimeo.com/")[1]?.split("?")[0];
    if (vimeoId) return <iframe src={`https://player.vimeo.com/video/${vimeoId}`} className="w-full aspect-video rounded-xl" allow="autoplay; fullscreen" title="Training Video" />;
  }
  return <a href={url} target="_blank" rel="noreferrer" className="text-xs text-[#E83E8C] hover:underline flex items-center gap-1"><Link2 size={12} /> {url}</a>;
};

// ─── Department selector ───
const DeptSelector = ({ selected, onChange, departments }) => (
  <div className="flex flex-wrap gap-2 mb-4" data-testid="dept-selector">
    <button onClick={() => onChange("")} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!selected ? "bg-[#1E1B4B] text-white" : "bg-white text-[#475569] border border-gray-200"}`}>All</button>
    {departments.map(d => (
      <button key={d.id} onClick={() => onChange(d.id)}
        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${selected === d.id ? "text-white" : "bg-white text-[#475569] border border-gray-200 hover:border-[#E83E8C]/30"}`}
        style={selected === d.id ? { backgroundColor: d.color } : {}}
        data-testid={`dept-btn-${d.id}`}>{d.label}</button>
    ))}
  </div>
);

// ─── MCQ Exam Component ───
const ExamView = ({ examId, moduleName, authAxios, onComplete }) => {
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [memberName, setMemberName] = useState("");

  useEffect(() => {
    authAxios.get(`${API_URL}/api/training/exams/${examId}`)
      .then(r => { setExam(r.data); setAnswers(new Array(r.data.questions.length).fill(-1)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [examId, authAxios]);

  const handleSubmit = async () => {
    if (!memberName.trim()) return;
    setSubmitting(true);
    try {
      const r = await authAxios.post(`${API_URL}/api/training/exams/${examId}/submit`, {
        answers, member_name: memberName,
      });
      setResult(r.data);
      if (r.data.passed && onComplete) onComplete();
    } catch { }
    setSubmitting(false);
  };

  if (loading) return <Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={20} />;
  if (!exam) return <p className="text-xs text-red-500">Exam not found</p>;

  if (result) {
    return (
      <div className="space-y-4" data-testid="exam-result">
        <div className={`text-center p-6 rounded-2xl border ${result.passed ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
          {result.passed ? <Award size={40} className="text-emerald-500 mx-auto mb-2" /> : <XCircle size={40} className="text-red-400 mx-auto mb-2" />}
          <p className="text-2xl font-bold">{result.score}%</p>
          <p className={`text-sm font-medium ${result.passed ? "text-emerald-700" : "text-red-600"}`}>
            {result.passed ? "Congratulations! You passed!" : `You need ${result.passing_score}% to pass. Try again!`}
          </p>
          <p className="text-xs text-gray-500 mt-1">{result.correct}/{result.total} correct</p>
          {result.badge && (
            <div className="mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold">
              <Trophy size={12} /> Certified — {moduleName}
            </div>
          )}
        </div>
        {result.results?.map((r, i) => (
          <div key={`q${i}-${r.is_correct ? "ok" : "no"}`} className={`p-3 rounded-xl border ${r.is_correct ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`}>
            <p className="text-xs font-medium text-[#1E1B4B] mb-1">{i + 1}. {r.question}</p>
            <p className="text-[10px] text-[#94a3b8]">{r.is_correct ? "Correct" : `Wrong — Correct: Option ${r.correct_answer + 1}`}{r.explanation ? ` — ${r.explanation}` : ""}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="exam-form">
      <div className="bg-indigo-50 rounded-xl p-3 flex items-center justify-between">
        <p className="text-xs font-medium text-indigo-700">
          <Clock size={12} className="inline mr-1" /> {exam.total_questions} questions — Pass: {exam.passing_score}%
        </p>
        <input value={memberName} onChange={e => setMemberName(e.target.value)} placeholder="Your name"
          className="px-3 py-1.5 rounded-lg border text-xs w-40" data-testid="exam-name-input" />
      </div>
      {exam.questions.map((q, qi) => (
        <div key={`q-${qi}-${(q.question || "").slice(0, 16)}`} className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`exam-q-${qi}`}>
          <p className="text-sm font-medium text-[#1E1B4B] mb-3">{qi + 1}. {q.question}</p>
          <div className="space-y-2">
            {q.options.map((opt, oi) => (
              <button key={`${qi}-${oi}-${(opt || "").slice(0, 8)}`} onClick={() => setAnswers(a => { const n = [...a]; n[qi] = oi; return n; })}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                  answers[qi] === oi ? "bg-[#E83E8C] text-white" : "bg-[#FAFAFA] text-[#475569] hover:bg-[#E83E8C]/5 border border-gray-100"
                }`}>{String.fromCharCode(65 + oi)}. {opt}</button>
            ))}
          </div>
        </div>
      ))}
      <button onClick={handleSubmit} disabled={submitting || !memberName.trim() || answers.includes(-1)}
        className="w-full py-3 rounded-xl bg-[#E83E8C] text-white font-medium text-sm disabled:opacity-40" data-testid="exam-submit-btn">
        {submitting ? "Submitting..." : "Submit Exam"}
      </button>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN: Enhanced Training Hub
// ═══════════════════════════════════════════════════════════════
const EnhancedTrainingHub = ({ authAxios }) => {
  const [departments, setDepartments] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDept, setSelectedDept] = useState("");
  const [selectedModule, setSelectedModule] = useState(null);
  const [view, setView] = useState("modules"); // modules, create, detail, exam
  const [certs, setCerts] = useState([]);

  // Create module form
  const [newModule, setNewModule] = useState({ title: "", department: "all", description: "", difficulty: "beginner", estimated_minutes: 30 });
  const [addContent, setAddContent] = useState({ type: "link", title: "", url: "" });

  const fetchModules = useCallback(async () => {
    setLoading(true);
    try {
      const [deptRes, modRes, certRes] = await Promise.all([
        authAxios.get(`${API_URL}/api/training/departments`),
        authAxios.get(`${API_URL}/api/training/modules${selectedDept ? `?department=${selectedDept}` : ""}`),
        authAxios.get(`${API_URL}/api/training/certifications`),
      ]);
      setDepartments(deptRes.data.departments || []);
      setModules(modRes.data.modules || []);
      setCerts(certRes.data.certifications || []);
    } catch { }
    setLoading(false);
  }, [authAxios, selectedDept]);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const handleCreateModule = async () => {
    if (!newModule.title.trim()) return;
    try {
      const r = await authAxios.post(`${API_URL}/api/training/modules`, newModule);
      setNewModule({ title: "", department: "all", description: "", difficulty: "beginner", estimated_minutes: 30 });
      setView("modules");
      fetchModules();
    } catch { }
  };

  const handleAddLink = async () => {
    if (!selectedModule || !addContent.title || !addContent.url) return;
    try {
      await authAxios.post(`${API_URL}/api/training/modules/${selectedModule.module_id}/content/link`, {
        title: addContent.title, url: addContent.url, type: addContent.type,
      });
      setAddContent({ type: "link", title: "", url: "" });
      // Refresh module
      const r = await authAxios.get(`${API_URL}/api/training/modules/${selectedModule.module_id}`);
      setSelectedModule(r.data);
    } catch { }
  };

  const handleFileUpload = async (file) => {
    if (!selectedModule || !file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", file.name);
      await authAxios.post(`${API_URL}/api/training/modules/${selectedModule.module_id}/content/upload`, formData);
      const r = await authAxios.get(`${API_URL}/api/training/modules/${selectedModule.module_id}`);
      setSelectedModule(r.data);
    } catch { }
  };

  const handleGenerateExam = async () => {
    if (!selectedModule) return;
    try {
      await authAxios.post(`${API_URL}/api/training/modules/${selectedModule.module_id}/exam/generate`, { num_questions: 5 });
      const r = await authAxios.get(`${API_URL}/api/training/modules/${selectedModule.module_id}`);
      setSelectedModule(r.data);
    } catch { }
  };

  const diffColors = { beginner: "bg-emerald-100 text-emerald-700", intermediate: "bg-amber-100 text-amber-700", advanced: "bg-red-100 text-red-700" };

  return (
    <div data-testid="enhanced-training-hub">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-[#1E1B4B] flex items-center gap-2">
          <GraduationCap size={18} className="text-[#E83E8C]" /> Training Center
        </h2>
        <div className="flex gap-2">
          {view !== "modules" && (
            <button onClick={() => { setView("modules"); setSelectedModule(null); }}
              className="px-3 py-1.5 rounded-lg text-xs border border-gray-200 text-[#475569]">Back to Modules</button>
          )}
          {view === "modules" && (
            <button onClick={() => setView("create")}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs font-medium" data-testid="create-module-btn">
              <Plus size={12} /> New Module
            </button>
          )}
        </div>
      </div>

      <DeptSelector selected={selectedDept} onChange={setSelectedDept} departments={departments} />

      {/* Certifications Banner */}
      {certs.length > 0 && view === "modules" && (
        <div className="bg-gradient-to-r from-amber-50 to-amber-100/50 rounded-xl border border-amber-200 p-3 mb-4 flex items-center gap-3">
          <Trophy size={20} className="text-amber-500" />
          <div>
            <p className="text-xs font-bold text-amber-800">{certs.length} Certification{certs.length > 1 ? "s" : ""} Earned</p>
            <p className="text-[10px] text-amber-600">{certs.map(c => c.module_title).join(", ")}</p>
          </div>
        </div>
      )}

      {/* Create Module Form */}
      {view === "create" && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3" data-testid="create-module-form">
          <h3 className="text-sm font-bold text-[#1E1B4B]">New Training Module</h3>
          <input value={newModule.title} onChange={e => setNewModule(m => ({ ...m, title: e.target.value }))}
            placeholder="Module title" className="w-full px-3 py-2 rounded-lg border text-sm" data-testid="module-title-input" />
          <textarea value={newModule.description} onChange={e => setNewModule(m => ({ ...m, description: e.target.value }))}
            placeholder="Description" rows={2} className="w-full px-3 py-2 rounded-lg border text-sm" />
          <div className="flex gap-2">
            <select value={newModule.department} onChange={e => setNewModule(m => ({ ...m, department: e.target.value }))}
              className="px-3 py-2 rounded-lg border text-xs" data-testid="module-dept-select">
              {departments.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
            </select>
            <select value={newModule.difficulty} onChange={e => setNewModule(m => ({ ...m, difficulty: e.target.value }))}
              className="px-3 py-2 rounded-lg border text-xs">
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
            <input type="number" value={newModule.estimated_minutes} onChange={e => setNewModule(m => ({ ...m, estimated_minutes: parseInt(e.target.value) || 30 }))}
              className="w-20 px-3 py-2 rounded-lg border text-xs" placeholder="Min" />
            <span className="text-[10px] text-[#94a3b8] self-center">min</span>
          </div>
          <button onClick={handleCreateModule} disabled={!newModule.title.trim()}
            className="px-4 py-2 rounded-lg bg-[#E83E8C] text-white text-xs font-medium disabled:opacity-40" data-testid="save-module-btn">Create Module</button>
        </div>
      )}

      {/* Module List */}
      {view === "modules" && (
        loading ? <Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={24} /> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {modules.map(mod => (
              <div key={mod.module_id} onClick={() => { setSelectedModule(mod); setView("detail"); }}
                className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-[#E83E8C]/20 cursor-pointer transition-all group" data-testid={`module-card-${mod.module_id}`}>
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-bold text-[#1E1B4B] group-hover:text-[#E83E8C] transition-colors">{mod.title}</h3>
                  <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${diffColors[mod.difficulty] || diffColors.beginner}`}>{mod.difficulty}</span>
                </div>
                {mod.description && <p className="text-[10px] text-[#94a3b8] line-clamp-2 mb-2">{mod.description}</p>}
                <div className="flex items-center gap-3 text-[10px] text-[#94a3b8]">
                  <span className="flex items-center gap-1"><BookOpen size={10} /> {mod.content?.length || 0} items</span>
                  <span className="flex items-center gap-1"><Clock size={10} /> {mod.estimated_minutes}m</span>
                  <span className="flex items-center gap-1"><Users size={10} /> {mod.completions || 0} certified</span>
                  {mod.exam_id && <span className="flex items-center gap-1 text-[#E83E8C]"><CheckCircle size={10} /> Exam</span>}
                </div>
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-50">
                  <span className="px-2 py-0.5 rounded text-[9px] bg-indigo-50 text-indigo-600 font-medium">
                    {departments.find(d => d.id === mod.department)?.label || mod.department}
                  </span>
                  <ChevronRight size={14} className="text-[#94a3b8] group-hover:text-[#E83E8C] transition-colors" />
                </div>
              </div>
            ))}
            {modules.length === 0 && (
              <div className="col-span-full text-center py-10 bg-white rounded-xl border border-gray-100">
                <GraduationCap size={32} className="text-[#E83E8C]/30 mx-auto mb-2" />
                <p className="text-sm text-[#94a3b8]">No training modules yet. Create your first!</p>
              </div>
            )}
          </div>
        )
      )}

      {/* Module Detail */}
      {view === "detail" && selectedModule && (
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="text-lg font-bold text-[#1E1B4B]">{selectedModule.title}</h3>
                <p className="text-xs text-[#94a3b8] mt-0.5">{selectedModule.description}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-[9px] font-bold ${diffColors[selectedModule.difficulty] || diffColors.beginner}`}>{selectedModule.difficulty}</span>
            </div>

            {/* Content List */}
            <div className="space-y-2 mb-4">
              {(selectedModule.content || []).map((c, i) => (
                <div key={c.content_id} className="bg-[#FAFAFA] rounded-xl p-3 border border-gray-50">
                  <div className="flex items-center gap-2 mb-2">
                    {c.type === "video" ? <Video size={14} className="text-red-500" /> : <FileText size={14} className="text-blue-500" />}
                    <span className="text-xs font-medium text-[#1E1B4B]">{c.title}</span>
                    <span className="text-[9px] text-[#94a3b8] ml-auto">{c.type}</span>
                  </div>
                  {c.source === "link" && c.url && (
                    <VideoEmbed url={c.url} embedType={c.embed_type} />
                  )}
                  {c.source === "upload" && (
                    <p className="text-[10px] text-[#94a3b8] flex items-center gap-1"><FileText size={10} /> {c.filename} ({Math.round((c.file_size || 0) / 1024)}KB)</p>
                  )}
                </div>
              ))}
            </div>

            {/* Add Content */}
            <div className="border-t border-gray-100 pt-3 space-y-2">
              <p className="text-[10px] font-bold text-[#94a3b8] uppercase">Add Content</p>
              <div className="flex gap-2">
                <select value={addContent.type} onChange={e => setAddContent(a => ({ ...a, type: e.target.value }))}
                  className="px-2 py-1.5 rounded-lg border text-xs">
                  <option value="video">Video Link</option>
                  <option value="presentation">PPT Link</option>
                  <option value="document">Document Link</option>
                </select>
                <input value={addContent.title} onChange={e => setAddContent(a => ({ ...a, title: e.target.value }))}
                  placeholder="Title" className="flex-1 px-3 py-1.5 rounded-lg border text-xs" data-testid="content-title-input" />
                <input value={addContent.url} onChange={e => setAddContent(a => ({ ...a, url: e.target.value }))}
                  placeholder="YouTube / Google Drive URL" className="flex-1 px-3 py-1.5 rounded-lg border text-xs" data-testid="content-url-input" />
                <button onClick={handleAddLink} disabled={!addContent.title || !addContent.url}
                  className="px-3 py-1.5 rounded-lg bg-indigo-500 text-white text-xs disabled:opacity-40" data-testid="add-link-btn">
                  <Link2 size={12} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#E83E8C] text-white text-xs cursor-pointer hover:bg-[#d1356f]" data-testid="upload-file-btn">
                  <Upload size={12} /> Upload File (Video/PPT/PDF)
                  <input type="file" className="hidden" accept=".mp4,.mov,.ppt,.pptx,.pdf,.doc,.docx"
                    onChange={e => handleFileUpload(e.target.files[0])} />
                </label>
                <span className="text-[9px] text-[#94a3b8]">Max 200MB</span>
              </div>
            </div>
          </div>

          {/* Exam Section */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2"><Award size={14} className="text-[#E83E8C]" /> Exam</h4>
              {!selectedModule.exam_id && (
                <button onClick={handleGenerateExam}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-500 text-white text-xs font-medium" data-testid="generate-exam-btn">
                  <Star size={12} /> AI Generate Exam
                </button>
              )}
            </div>
            {selectedModule.exam_id ? (
              <div>
                <p className="text-xs text-emerald-600 mb-3 flex items-center gap-1"><CheckCircle size={12} /> Exam attached ({selectedModule.exam?.total_questions || "?"} questions, pass: {selectedModule.exam?.passing_score || 70}%)</p>
                <button onClick={() => setView("exam")}
                  className="px-4 py-2 rounded-lg bg-[#E83E8C] text-white text-xs font-medium" data-testid="take-exam-btn">Take Exam</button>
              </div>
            ) : (
              <p className="text-xs text-[#94a3b8]">No exam yet. Click "AI Generate Exam" to create one automatically from the module content.</p>
            )}
          </div>
        </div>
      )}

      {/* Exam View */}
      {view === "exam" && selectedModule?.exam_id && (
        <ExamView examId={selectedModule.exam_id} moduleName={selectedModule.title} authAxios={authAxios} onComplete={fetchModules} />
      )}
    </div>
  );
};

export default EnhancedTrainingHub;
